<template>
  <div class="pdf-viewer">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>
    
    <!-- 错误信息 -->
    <div v-else-if="error" class="error">
      <div class="error-icon">⚠️</div>
      <div class="error-message">{{ error }}</div>
      <button @click="loadReport" class="retry-btn">重试</button>
    </div>
    
    <!-- PDF预览 -->
    <div v-else-if="report && fileUrl" class="pdf-container">
      <EnhancedPdfViewer :pdf-url="fileUrl" />
    </div>
    
    <!-- 无法预览的情况 -->
    <div v-else class="no-preview">
      <div class="no-preview-icon">📄</div>
      <div class="no-preview-text">无法预览此文件</div>
      <a :href="downloadUrl" class="download-link" target="_blank">下载文件</a>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import EnhancedPdfViewer from '@/components/EnhancedPdfViewer.vue'

export default {
  name: 'PdfViewer',
  components: {
    EnhancedPdfViewer
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      report: null,
      loading: true,
      error: null,
      fileUrl: null
    }
  },
  computed: {
    downloadUrl() {
      if (!this.report) return '#'
      return `/api/download-file/${this.id}`
    }
  },
  async mounted() {
    await this.loadReport()
  },
  methods: {
    async loadReport() {
      try {
        this.loading = true
        this.error = null
        
        console.log('开始加载报告，ID:', this.id)
        
        // 添加时间戳防缓存
        const timestamp = Date.now()
        const response = await axios.get(`/api/report/${this.id}?_t=${timestamp}`)
        console.log('报告API响应:', response.data)
        
        if (response.data.success) {
          this.report = response.data.data
          console.log('报告数据:', this.report)
          
          // 检查是否有附件
          if (!this.report.attachment) {
            console.error('报告没有附件')
            throw new Error('报告没有附件')
          }
          
          console.log('附件信息:', this.report.attachment)
          
          // 设置文件URL - 使用代理URL避免跳转下载问题，添加时间戳防缓存
          this.fileUrl = `/api/proxy-file/${this.id}?_t=${timestamp}`
          console.log('使用代理URL:', this.fileUrl)
        } else {
          console.error('API返回失败:', response.data)
          throw new Error(response.data.message || '获取报告信息失败')
        }
      } catch (error) {
        console.error('加载报告错误:', error)
        
        if (error.response) {
          console.error('HTTP错误响应:', error.response)
          switch (error.response.status) {
            case 404:
              this.error = '报告不存在'
              break
            case 500:
              this.error = '服务器错误，请稍后重试'
              break
            default:
              this.error = error.response.data?.message || '加载失败'
          }
        } else if (error.message) {
          this.error = error.message
        } else {
          this.error = '网络错误，请检查网络连接'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #f5f5f5;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #666;
  height: 100%;
  background: white;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.9rem;
}

.error {
  text-align: center;
  padding: 3rem 2rem;
  color: #e74c3c;
  background: #fdf2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
  max-width: 500px;
  margin: 2rem auto;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-message {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.retry-btn {
  background: #e74c3c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: #c0392b;
}

.pdf-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.no-preview {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 500px;
  margin: 2rem auto;
}

.no-preview-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  opacity: 0.6;
}

.no-preview-text {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
}

.download-link {
  background: #28a745;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  display: inline-block;
  font-weight: 500;
  transition: background-color 0.2s;
}

.download-link:hover {
  background: #218838;
  color: white;
  text-decoration: none;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .pdf-viewer {
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: #ffffff;
  }
  
  .pdf-container {
    height: 100%;
    overflow: hidden;
    position: relative;
  }
  
  .error,
  .no-preview {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 2rem 1rem;
    margin: 0;
    border-radius: 8px;
    text-align: center;
    background: #ffffff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1001;
    max-width: 90vw;
  }
  
  .loading {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 1rem;
    z-index: 1001;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .error,
  .no-preview {
    max-width: 95vw;
    padding: 1.5rem 0.75rem;
  }
  
  .error-icon,
  .no-preview-icon {
    font-size: 2.5rem;
  }
  
  .error-message,
  .no-preview-text {
    font-size: 1rem;
  }
  
  .retry-btn,
  .download-link {
    width: 100%;
    padding: 0.75rem 1.2rem;
    font-size: 0.9rem;
  }
}
</style>